import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { statusLabel, statusTone, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { FactStrip } from "@/components/app/patterns/fact-strip";
import { Fact } from "@/components/app/patterns/fact";
import { RecordHeader } from "@/components/app/patterns/record-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { AutoDraftSwitch } from "./-components/auto-draft-switch";
import { ChargeMenu } from "./-components/charge-admin";
import { ChargeInvoices } from "@/routes/_authenticated/_financials/charges/-components/charge-invoices";
import { DriftCard } from "@/routes/_authenticated/_financials/charges/-components/drift-card";
import { FeeCard } from "@/routes/_authenticated/_financials/charges/-components/fee-card";
import { LockedAmounts } from "./-components/charge-sections";
import { MissedPeriods } from "@/routes/_authenticated/_financials/charges/-components/missed-periods";
import { chargeTitle, monthName, scheduleShort } from "./-components/charge-text";
import { EditChargeDialog } from "./-components/edit-charge-dialog";
import { BilledResidentAvatar } from "../-components/billed-resident-avatar";
import { BilledResidentLink } from "@/routes/_authenticated/_financials/-components/billed-resident-link";
import { moneyReaders } from "@/lib/query/query-readers";

export const Route = createFileRoute("/_authenticated/_financials/charges/$chargeId")({
  component: ChargeDetail,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One charge: what it locks, what it has raised, and where it has drifted. */
function ChargeDetail() {
  const { chargeId } = Route.useParams();
  const queryClient = useQueryClient();

  const charge = useQuery(orpc.financials.charges.get.queryOptions({ input: { chargeId } }));
  // Asked of the charge's own building, as the API asks it.
  const canWrite = useCan("financial:manage", charge.data?.facilityId);
  const drift = useQuery(orpc.financials.charges.drift.queryOptions({ input: { chargeId } }));
  const missed = useQuery(
    orpc.financials.charges.missedPeriods.queryOptions({ input: { chargeId } }),
  );
  // Whose charge this is: the charge carries only the id. Unread, the header
  // simply goes without the name.
  const residentId = charge.data?.residentId ?? "";
  const resident = useQuery({
    ...orpc.care.residents.get.queryOptions({ input: { residentId } }),
    enabled: Boolean(residentId),
  });

  const update = useMutation(
    orpc.financials.charges.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Charge updated");
        await invalidateKeys(queryClient, moneyReaders());
      },
      onError: fail,
    }),
  );

  if (charge.isPending) return <Skeleton className="h-64 w-full" />;
  // A blank page after a failed read reads as a charge with nothing on it.
  if (!charge.data)
    return (
      <>
        <PageHeader
          title="Charge"
          meta={
            <Link to="/charges" className="hover:underline">
              Back to charge series
            </Link>
          }
        />
        <ReadFailed
          message="This charge could not be loaded."
          error={charge.error}
          onRetry={() => void charge.refetch()}
          retrying={charge.isFetching}
        />
      </>
    );

  const c = charge.data;
  const locked = (c.customAmounts ?? {}) as Record<string, string>;
  const drifted = (drift.data ?? []).filter((row) => row.changed);
  const series = c.kind === "series";
  const title = c.chargeNumber ?? "Charge";

  return (
    <>
      <RecordHeader
        back={{ label: "Charges", link: { to: "/charges" } }}
        media={resident.data ? <BilledResidentAvatar resident={resident.data} /> : null}
        title={title}
        titleId="charge-title"
        badges={<Badge variant={statusTone(c.status)}>{statusLabel(c.status)}</Badge>}
        facts={[
          resident.data ? (
            <BilledResidentLink key="resident" residentId={residentId} resident={resident.data} />
          ) : null,
          series ? "Recurring" : "One-off",
        ]}
        actions={
          canWrite ? (
            <>
              {/* Auto-draft: whether the invoices it raises are paid by
                  drafting a saved card or bank account on their due date. */}
              <AutoDraftSwitch
                chargeId={chargeId}
                residentId={c.residentId}
                enabled={c.autoDraftEnabled}
              />
              {series && (c.status === "active" || c.status === "paused") ? (
                <EditChargeDialog key={String(c.updatedAt)} charge={c} />
              ) : null}
              {series && c.status === "active" ? (
                <Button
                  variant="outline"
                  onClick={() => update.mutate({ chargeId, status: "paused" })}
                >
                  Pause series
                </Button>
              ) : series && c.status === "paused" ? (
                <Button
                  variant="outline"
                  onClick={() => update.mutate({ chargeId, status: "active" })}
                >
                  Resume series
                </Button>
              ) : null}
              <ChargeMenu chargeId={chargeId} name={title} />
            </>
          ) : null
        }
        strip={
          <FactStrip>
            <Fact label="Bills" value={<span title={chargeTitle(c)}>{chargeTitle(c)}</span>} />
            <Fact label="Schedule" value={scheduleShort(c)} />
            {series ? (
              <Fact
                label="Started"
                value={c.startPeriod ? monthName(c.startPeriod) : "At creation"}
              />
            ) : null}
            <Fact label="Last billed" value={c.lastRunPeriod ? monthName(c.lastRunPeriod) : "-"} />
            {series ? (
              <Fact label="Ends" value={c.endPeriod ? monthName(c.endPeriod) : "No end date"} />
            ) : null}
          </FactStrip>
        }
      />

      <LockedAmounts locked={locked} />

      {c.fee ? <FeeCard chargeId={chargeId} fee={c.fee} canWrite={canWrite} /> : null}

      {drifted.length > 0 ? (
        <DriftCard
          drifted={drifted}
          canWrite={canWrite}
          onApply={() =>
            update.mutate({
              chargeId,
              customAmounts: {
                ...locked,
                ...Object.fromEntries(drifted.map((row) => [row.key, row.currentAmount])),
              },
            })
          }
        />
      ) : null}

      {(missed.data?.periods.length ?? 0) > 0 ? (
        <MissedPeriods
          chargeId={chargeId}
          periods={missed.data?.periods ?? []}
          canWrite={canWrite}
        />
      ) : null}

      <ChargeInvoices invoices={c.invoices} />
    </>
  );
}
